import { useState } from 'react';
import { Star, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { reviewsAPI } from '../services/api';
import { toast } from 'sonner';

  /**
 * Customer review submission form.
 * Props:
 *   - defaultVehicle (string): pre-fill vehicle field (used on product detail page)
 *   - defaultProductImage (string): pre-fill productImage field
 *   - productId (string): associate review with a specific product
 *   - onSubmitted (fn): callback after successful submit
 *   - compact (bool): smaller layout for embedding
 */
const ReviewForm = ({ defaultVehicle = '', defaultProductImage = '', productId = null, onSubmitted, compact = false }) => {
  const [name, setName] = useState('');
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [vehicle, setVehicle] = useState(defaultVehicle);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hover, setHover] = useState(0);

  const reset = () => {
    setName(''); setRating(5); setTitle('');
    setVehicle(defaultVehicle); setText('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !title.trim() || !text.trim()) {
      toast.error('Please fill in your name, a short title, and your review');
      return;
    }
    if (text.trim().length < 20) {
      toast.error('Your review needs to be at least 20 characters');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        rating,
        title: title.trim(),
        vehicle: vehicle.trim() || 'Not specified',
        text: text.trim(),
        productImage: defaultProductImage,
      };
      if (productId) payload.productId = productId;
      const created = await reviewsAPI.create(payload);
      toast.success('Thanks! Your review has been submitted.');
      reset();
      onSubmitted?.(created);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Sorry, your review could not be submitted');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card data-testid="review-form">
      <CardContent className={compact ? 'p-4' : 'p-6'}>
        <h3 className="text-xl font-bold mb-1">Leave a Review</h3>
        <p className="text-sm text-gray-500 mb-4">Share your experience with other BlueSpark customers.</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Your Name *</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. John S" maxLength={60} data-testid="review-name" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Vehicle {defaultVehicle ? '' : '(optional)'}</label>
              <Input value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="e.g. BMW 320d 2020" maxLength={120} data-testid="review-vehicle" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Your Rating *</label>
            <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
              {[1,2,3,4,5].map((i) => {
                const active = (hover || rating) >= i;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setRating(i)}
                    onMouseEnter={() => setHover(i)}
                    className="p-1 hover:scale-110 transition-transform"
                    aria-label={`Rate ${i} stars`}
                    data-testid={`review-rating-${i}`}
                  >
                    <Star className={`h-7 w-7 ${active ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                  </button>
                );
              })}
              <span className="ml-2 text-sm text-gray-600">{rating}/5</span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Review Title *</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Massive difference!" maxLength={120} data-testid="review-title" />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Your Review * <span className="text-gray-400">(min 20 chars)</span></label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Tell us about your experience with the tuning module…"
              rows={4}
              maxLength={2000}
              className="w-full p-2 border rounded text-sm"
              data-testid="review-text"
            />
            <p className="text-xs text-gray-400 mt-1">{text.length}/2000</p>
          </div>

          <Button type="submit" disabled={submitting} className="w-full md:w-auto" data-testid="review-submit">
            <Send className="h-4 w-4 mr-2" />
            {submitting ? 'Submitting…' : 'Submit Review'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ReviewForm;
